'use client';

import {
  type Dispatch,
  type FocusEvent,
  type HTMLProps,
  type MouseEvent,
  type SetStateAction,
  createContext,
  forwardRef,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import {
  FloatingFocusManager,
  FloatingList,
  FloatingNode,
  FloatingPortal,
  FloatingTree,
  autoUpdate,
  flip,
  offset,
  safePolygon,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useFloatingNodeId,
  useFloatingParentNodeId,
  useFloatingTree,
  useHover,
  useInteractions,
  useListItem,
  useListNavigation,
  useMergeRefs,
  useRole,
  useTypeahead,
} from '@floating-ui/react';
import classnames from 'classnames';

import { DropdownIcon } from '../dropdown-icon';
import { Icon } from '../icon';
import type {
  DropdownMenuItemProps,
  DropdownMenuProps,
} from './DropdownMenu.types';

import styles from './DropdownMenu.module.scss';

export type DropdownMenuContextType = {
  getItemProps: (userProps?: HTMLProps<HTMLElement>) => Record<string, unknown>;
  activeIndex: number | null;
  setActiveIndex: Dispatch<SetStateAction<number | null>>;
  setHasFocusInside: Dispatch<SetStateAction<boolean>>;
  isOpen: boolean;
};

const stopPropagation = (event: MouseEvent) => event.stopPropagation();

export const MenuContext = createContext<DropdownMenuContextType>({
  getItemProps: () => ({}),
  activeIndex: null,
  setActiveIndex: () => {},
  setHasFocusInside: () => {},
  isOpen: false,
});

export const DropdownMenuComponent = forwardRef<
  HTMLButtonElement,
  DropdownMenuProps
>(
  (
    {
      children,
      label,
      icon,
      prefix,
      suffix,
      placement = 'bottom-start',
      className,
      style,
      testId = 'ui-dropdown-menu',
      ...props
    },
    forwardedRef,
  ) => {
    const [isOpen, setIsOpen] = useState(false);
    const [hasFocusInside, setHasFocusInside] = useState(false);
    const [activeIndex, setActiveIndex] = useState<number | null>(null);

    const elementsRef = useRef<Array<HTMLButtonElement | null>>([]);
    const labelsRef = useRef<Array<string | null>>([]);
    const parent = useContext(MenuContext);

    const tree = useFloatingTree();
    const nodeId = useFloatingNodeId();
    const parentId = useFloatingParentNodeId();
    const item = useListItem();

    const isNested = parentId != null;

    const { floatingStyles, refs, context } = useFloating<HTMLButtonElement>({
      nodeId,
      open: isOpen,
      onOpenChange: setIsOpen,
      placement: isNested ? 'right-start' : placement,
      middleware: [
        offset({
          mainAxis: isNested ? 0 : 4,
          alignmentAxis: isNested ? -4 : 0,
        }),
        flip(),
        shift(),
      ],
      whileElementsMounted: autoUpdate,
    });

    const hover = useHover(context, {
      enabled: isNested,
      delay: { open: 75 },
      handleClose: safePolygon({ blockPointerEvents: true }),
    });
    const click = useClick(context, {
      event: 'mousedown',
      toggle: !isNested,
      ignoreMouse: isNested,
    });
    const role = useRole(context, { role: 'menu' });
    const dismiss = useDismiss(context, { bubbles: true });
    const listNavigation = useListNavigation(context, {
      listRef: elementsRef,
      activeIndex,
      nested: isNested,
      onNavigate: setActiveIndex,
    });
    const typeahead = useTypeahead(context, {
      listRef: labelsRef,
      onMatch: isOpen ? setActiveIndex : undefined,
      activeIndex,
    });

    const { getReferenceProps, getFloatingProps, getItemProps } =
      useInteractions([hover, click, role, dismiss, listNavigation, typeahead]);

    // Event emitter allows you to communicate across tree components.
    // This effect closes all menus when an item gets clicked anywhere
    // in the tree.
    useEffect(() => {
      if (!tree) return;

      function handleTreeClick() {
        setIsOpen(false);
      }

      function onSubMenuOpen(event: { nodeId: string; parentId: string }) {
        if (event.nodeId !== nodeId && event.parentId === parentId) {
          setIsOpen(false);
        }
      }

      tree.events.on('click', handleTreeClick);
      tree.events.on('menuopen', onSubMenuOpen);

      return () => {
        tree.events.off('click', handleTreeClick);
        tree.events.off('menuopen', onSubMenuOpen);
      };
    }, [tree, nodeId, parentId]);

    useEffect(() => {
      if (isOpen && tree) {
        tree.events.emit('menuopen', { parentId, nodeId });
      }
    }, [tree, isOpen, nodeId, parentId]);

    const triggerProps = {
      ...props,
      onFocus(event: FocusEvent<HTMLButtonElement>) {
        props.onFocus?.(event);
        setHasFocusInside(false);
        parent.setHasFocusInside(true);
      },
    };
    // A nested trigger is also a menu item, so its props must flow through the
    // parent menu's `getItemProps` for list navigation. A root trigger has no
    // such parent (the default context drops everything), so forward its DOM
    // props — `aria-label`, `onClick`, etc. — directly.
    const referenceProps = isNested
      ? parent.getItemProps(triggerProps)
      : triggerProps;

    return (
      <FloatingNode id={nodeId}>
        <button
          ref={useMergeRefs([refs.setReference, item.ref, forwardedRef])}
          type="button"
          tabIndex={
            !isNested ? undefined : parent.activeIndex === item.index ? 0 : -1
          }
          role={isNested ? 'menuitem' : undefined}
          data-open={isOpen ? '' : undefined}
          data-nested={isNested ? '' : undefined}
          data-focus-inside={hasFocusInside ? '' : undefined}
          data-testid={testId}
          className={classnames(
            isNested ? styles['menuitem'] : styles['rootmenu'],
            className,
          )}
          style={style}
          {...getReferenceProps(referenceProps)}
        >
          <div className={styles['content']}>
            {icon && <Icon icon={icon} size="sm" />}
            {prefix ? (
              <span
                className={styles['prefix-wrapper']}
                onClick={stopPropagation}
              >
                {prefix}
              </span>
            ) : null}
            {label ? <span className={styles['label']}>{label}</span> : null}
            {suffix ? (
              <span
                className={styles['suffix-wrapper']}
                onClick={stopPropagation}
              >
                {suffix}
              </span>
            ) : null}
          </div>
          {isNested && (
            <DropdownIcon closed={true} className={styles['submenu-icon']} />
          )}
        </button>
        <MenuContext.Provider
          value={{
            activeIndex,
            setActiveIndex,
            getItemProps,
            setHasFocusInside,
            isOpen,
          }}
        >
          <FloatingList elementsRef={elementsRef} labelsRef={labelsRef}>
            {isOpen && (
              <FloatingPortal>
                <FloatingFocusManager
                  context={context}
                  modal={false}
                  initialFocus={isNested ? -1 : 0}
                  returnFocus={!isNested}
                >
                  <div
                    ref={refs.setFloating}
                    className={styles['menu']}
                    style={floatingStyles}
                    data-testid={`${testId}-menu`}
                    {...getFloatingProps()}
                  >
                    {children}
                  </div>
                </FloatingFocusManager>
              </FloatingPortal>
            )}
          </FloatingList>
        </MenuContext.Provider>
      </FloatingNode>
    );
  },
);

DropdownMenuComponent.displayName = 'DropdownMenuComponent';

export const DropdownMenuItem = forwardRef<
  HTMLButtonElement,
  DropdownMenuItemProps
>(
  (
    {
      label,
      disabled,
      icon,
      prefix,
      suffix,
      className,
      style,
      testId = 'ui-dropdown-menu-item',
      ...props
    },
    forwardedRef,
  ) => {
    const menu = useContext(MenuContext);
    const item = useListItem({ label: disabled ? null : label });
    const tree = useFloatingTree();
    const isActive = item.index === menu.activeIndex;

    return (
      <button
        {...props}
        ref={useMergeRefs([item.ref, forwardedRef])}
        type="button"
        role="menuitem"
        data-testid={testId}
        className={classnames(styles['menuitem'], className)}
        style={style}
        tabIndex={isActive ? 0 : -1}
        disabled={disabled}
        {...menu.getItemProps({
          onClick(event: MouseEvent<HTMLButtonElement>) {
            props.onClick?.(event);
            tree?.events.emit('click');
          },
          onFocus(event: FocusEvent<HTMLButtonElement>) {
            props.onFocus?.(event);
            menu.setHasFocusInside(true);
          },
        })}
      >
        {icon && <Icon icon={icon} size="sm" />}
        {prefix ? (
          <span className={styles['prefix-wrapper']} onClick={stopPropagation}>
            {prefix}
          </span>
        ) : null}
        {label}
        {suffix ? (
          <span className={styles['suffix-wrapper']} onClick={stopPropagation}>
            {suffix}
          </span>
        ) : null}
      </button>
    );
  },
);

DropdownMenuItem.displayName = 'DropdownMenuItem';

export const DropdownMenu = forwardRef<HTMLButtonElement, DropdownMenuProps>(
  (props, ref) => {
    const parentId = useFloatingParentNodeId();

    if (parentId === null) {
      return (
        <FloatingTree>
          <DropdownMenuComponent {...props} ref={ref} />
        </FloatingTree>
      );
    }

    return <DropdownMenuComponent {...props} ref={ref} />;
  },
);

DropdownMenu.displayName = 'DropdownMenu';
